import { useEffect, useRef } from 'react'
import { useJobProgress } from '../lib/useJobProgress'
import type { MyJobState } from '../api/jobs'
import { IconButton, LinearProgress } from './ui'
import './shared.css'

/**
 * F-066 (TASK-0320, REQ-228-002): avviso in basso a destra che mostra l'avanzamento di un job lanciato
 * dall'utente. Non blocca nulla: l'utente continua a lavorare e la pagina e' interamente usabile; quando il
 * job finisce l'avviso mostra l'esito e `onFinished` permette alla pagina di ricaricarsi.
 */
export interface JobProgressToastProps {
  jobId: number
  /** Descrizione dell'operazione, per esempio "Cambio stato di 120 lead". */
  title: string
  onClose: () => void
  onFinished?: (state: MyJobState) => void
}

const RESULT_LABELS: Record<string, string> = {
  applied: 'applicati',
  alreadyApplied: 'già a posto',
  invalidTransition: 'non ammessi',
  notFound: 'non trovati',
}

function resultSummary(result: Record<string, unknown> | null): string | null {
  if (!result) return null
  const parts = Object.entries(RESULT_LABELS)
    .filter(([key]) => typeof result[key] === 'number')
    .map(([key, label]) => `${String(result[key])} ${label}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

export function JobProgressToast({ jobId, title, onClose, onFinished }: JobProgressToastProps) {
  const { state, error, finished } = useJobProgress(jobId)
  const notified = useRef(false)

  useEffect(() => {
    if (finished && state && !notified.current) {
      notified.current = true
      onFinished?.(state)
    }
  }, [finished, state, onFinished])

  const total = state?.total ?? 0
  const done = state?.done ?? 0
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0
  const failed = state?.status === 'failed'
  const cancelled = state?.status === 'cancelled'
  const summary = finished && !failed && !cancelled ? resultSummary(state?.result ?? null) : null

  return (
    <div className="ecrm-job-toast-host">
      <div className="ecrm-job-toast md-snackbar" role="status" aria-live="polite">
        <div className="ecrm-job-toast__head">
          <span className="ecrm-job-toast__title md-snackbar__message">{title}</span>
          <IconButton icon="close" size="sm" aria-label="Chiudi" className="md-snackbar__close" tooltip={false} onClick={onClose} />
        </div>
        {error ? (
          <div className="ecrm-job-toast__text--warn">
            Non riesco più a leggere l'avanzamento ({error}). L'operazione continua sul server.
          </div>
        ) : failed ? (
          <div className="ecrm-job-toast__text--error">
            L'operazione non è riuscita{state?.error ? `: ${state.error}` : ''}. Un amministratore può
            ritentarla dalla console dei job.
          </div>
        ) : cancelled ? (
          <div>L'operazione è stata annullata.</div>
        ) : finished ? (
          <div>Completata{summary ? `: ${summary}` : ''}.</div>
        ) : (
          <>
            <div>{total > 0 ? `${done} di ${total}` : state ? 'In coda…' : 'Avvio…'}</div>
            <LinearProgress value={total > 0 ? percent : 5} aria-label={title} />
          </>
        )}
      </div>
    </div>
  )
}
